// import * as docx from "docx";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, convertMillimetersToTwip, ImageRun } from "docx";

import phoneImg from "src/files/doc-img-phone-optimised.jpg?inline";
import emailImg from "src/files/doc-img-email-optimised.jpg?inline";

import { format, parseISO } from 'date-fns'


export default function generateWordResume(item) {
    const {
        awards,
        educationCerts,
        qualifications,
        userProfile,
        employmentExp,
        volunteeringExp,
        workExp,
        portfolio
    } = item
    const {
        name,
        longIntro,
        shortIntro,
        qualitiesList,
        skillsList,
        extraCurricularList
    } = userProfile

    function sizeInPx(int) {
        return int * 2
    }

    function linebreak() {
        return new Paragraph({
            text: "",
        })
    }

    function friendlyDate(isoDateString) {
        return format(parseISO(isoDateString), 'MMM yyyy')
    }

    function titleMapping(string) {
        switch (string) {
            case "Qualification":
                return 'Qualifications & Licences'
            case "Award":
                return 'Awards'
            case "Employment":
                return 'Employment'
            case "Work experience":
                return "Work Experience"
            case "Volunteering":
                return "Volunteering Experience"
            case "Extra-curricular":
                return 'EXTRA-CURRICULAR EXPERIENCES'
            case "Referee":
                return "Referees"
            case "Portfolio":
                return "Portfolio"
        }
    }

    const filename = `CV-${name.trim().replace(/\s+/g, '-')}.docx`;

    const endDateFriendly = (date) => {
        return date ? ' - ' + friendlyDate(date) : ''
    }

    const qualitiesListItems = qualitiesList ? qualitiesList.map((qualityItem) => {
        return new Paragraph({
            text: qualityItem,
            bullet: {
                level: 0
            }
        })
    }) : [];

    const educationCertsItems = educationCerts ? educationCerts.flatMap((certObj) => {
        const { acaTitle, acaInstitution, startDate, endDate, acaPresent } = certObj;

        return [
            new Paragraph({
                text: acaTitle,
                heading: HeadingLevel.HEADING_4,
            }),
            new Paragraph({
                text: acaInstitution,
                heading: HeadingLevel.HEADING_5,
            }),
            new Paragraph({
                children: [
                    new TextRun({
                        text: friendlyDate(startDate),
                    }),
                    new TextRun({
                        text: `${acaPresent && acaPresent.length > 0 ? ' - Present' : endDateFriendly(endDate)}`,
                    }),
                ]
            }),
            linebreak()
        ]
    }) : [];

    let allExp = [employmentExp, workExp, volunteeringExp].filter(n => n);

    const allExpText = allExp ? allExp.flatMap((experienceArray) => {
        return experienceArray.flatMap((expObj, experienceObjIndex) => {
            const {
                startDate,
                endDate,
                expPresent,
                expTitle,
                expOrg,
                expResponsibilities,
                expType,
            } = expObj
            return [
                experienceObjIndex === 0 ? new Paragraph({
                    text: titleMapping(expType[0]),
                    heading: HeadingLevel.HEADING_2,
                }) : false,
                new Paragraph({
                    text: expTitle,
                    heading: HeadingLevel.HEADING_4,
                }),
                new Paragraph({
                    text: expOrg,
                    heading: HeadingLevel.HEADING_5,
                }),
                new Paragraph({
                    children: [
                        new TextRun({
                            text: friendlyDate(startDate),
                        }),
                        new TextRun({
                            text: `${expPresent && expPresent.length > 0 ? ' - Present' : endDateFriendly(endDate)}`,
                        }),
                    ]
                }),
                linebreak(),
                ...expResponsibilities.map((responsibilityItem) => {
                    return new Paragraph({
                        text: responsibilityItem,
                        bullet: {
                            level: 0
                        }
                    })
                }),
                linebreak(),
                linebreak(),
            ]
        })
    }) : [];

    const extraCurricularListItems = extraCurricularList ? extraCurricularList.flatMap((extraCurItem, extraCurIndex) => {
        const outputArray = []
        if (extraCurIndex === 0) {
            outputArray.push(
                new Paragraph({
                    text: "Extra-curricular activities",
                    heading: HeadingLevel.HEADING_3,
                })
            )
            outputArray.push(
                new Paragraph({
                    text: '___',
                    color: '152853',
                })
            )
        }
        outputArray.push(
            new Paragraph({
                text: extraCurItem,
                bullet: {
                    level: 0
                }
            })
        )
        return outputArray
    }) : [];

    const allAcheivements = [qualifications, awards].filter(n => n);

    const acheivementItems = allAcheivements ? allAcheivements.flatMap((array) => {
        return array.flatMap((qualificationItem, index) => {
            const {
                achieveDescription,
                achieveTitle,
                achieveType,
            } = qualificationItem
            return [
                index === 0 ? new Paragraph({
                    text: titleMapping(achieveType[0]),
                    heading: HeadingLevel.HEADING_3,
                }) : false,
                index === 0 ? new Paragraph({
                    text: '___',
                    color: '152853',
                }) : false,
                new Paragraph({
                    text: achieveTitle,
                    heading: HeadingLevel.HEADING_5,
                }),
                new Paragraph({
                    text: achieveDescription
                }),
                linebreak(),
                linebreak(),
            ]
        })
    }) : [];

    const skillsListItems = skillsList ? skillsList.flatMap((skillItem, skillIndex) => {
        const outputArray = []
        if (skillIndex === 0) {
            outputArray.push(
                new Paragraph({
                    text: "Skills",
                    heading: HeadingLevel.HEADING_3,
                })
            )
            outputArray.push(
                new Paragraph({
                    text: '___',
                    color: '152853',
                })
            )
        }
        outputArray.push(
            new Paragraph({
                text: skillItem,
                bullet: {
                    level: 0
                }
            })
        )
        return outputArray
    }) : [];

    const doc = new Document({
        styles: {
            default: {
                heading1: {
                    run: {
                        size: sizeInPx(20),
                        color: '16254a',
                        bold: true,
                        font: "Calibri",
                    },
                    paragraph: {
                        spacing: {
                            after: convertMillimetersToTwip(5)
                        }
                    }
                },
                heading2: {
                    run: {
                        size: sizeInPx(13),
                        color: '0daa89',
                        bold: true,
                        allCaps: true,
                        font: "Calibri",
                    },
                    paragraph: {
                        spacing: {
                            after: convertMillimetersToTwip(3)
                        },
                        border: {
                            bottom: {
                                size: sizeInPx(5),
                                value: 'single',
                                space: 5,
                                color: '0daa89',
                            }
                        }
                    }
                },
                heading3: {
                    run: {
                        size: sizeInPx(12),
                        color: '16254a',
                        bold: true,
                        allCaps: true,
                        font: "Calibri",
                    },
                },
                heading4: {
                    run: {
                        size: sizeInPx(10),
                        bold: true,
                        allCaps: true,
                        font: "Calibri",
                    },
                },
                heading5: {
                    run: {
                        color: '000000',
                        font: "Calibri",
                    },
                },
                heading6: {},
            },
            paragraphStyles: [{
                    name: 'Normal',
                    run: {
                        font: "Calibri light",
                    },
                },
                {
                    id: "subHeader",
                    name: "Sub Header",
                    run: {
                        font: "Calibri",
                        color: '16254a'
                    }
                }
            ]
        },
        sections: [{
            properties: {},
            children: [
                new Paragraph({
                    text: name,
                    heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                    text: shortIntro,
                    style: "subHeader"
                }),
                linebreak(),
                new Paragraph({
                    children: [
                        new ImageRun({
                            data: emailImg,
                            transformation: {
                                width: 25,
                                height: 25,
                            },
                        }),
                        new TextRun({
                            text: " Email address",
                        }),
                    ],
                    spacing: {
                        after: 100,
                    },
                }),
                new Paragraph({
                    children: [
                        new ImageRun({
                            data: phoneImg,
                            transformation: {
                                width: 25,
                                height: 25,
                            },
                        }),
                        new TextRun({
                            text: " Telephone No.",
                        }),
                    ],
                    spacing: {
                        after: 100,
                    },
                }),
                new Paragraph({
                    text: "Personal Qualities",
                    heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({
                    text: longIntro,
                }),
                linebreak(),
                ...qualitiesListItems,
                linebreak(),
                new Paragraph({
                    text: "Education",
                    heading: HeadingLevel.HEADING_2,
                }),
                // EDUCATION SECTION
                ...educationCertsItems,
                // EMPLOYMENT / WORK EXPERIENCE / VOLUNTEERING 
                ...allExpText,
                // EXTRA-CURRICULAR ACTIVITIES
                ...extraCurricularListItems,
                linebreak(),
                ...acheivementItems,
                ...skillsListItems,
                linebreak(),
                ...[portfolio].flatMap((array) => {
                    return [...array.flatMap((refereeItem, index) => {
                        const { supInfo, supTitle, supType } = refereeItem
                        const outputArray = []
                        if (index === 0) {
                            outputArray.push(
                                new Paragraph({
                                    text: titleMapping(supType[0]),
                                    heading: HeadingLevel.HEADING_3,
                                })
                            )
                            outputArray.push(
                                new Paragraph({
                                    text: '___',
                                    color: '152853',
                                })
                            )
                        }
                        outputArray.push(
                            new Paragraph({
                                text: supInfo
                            })
                        )
                        outputArray.push(
                            new Paragraph({
                                text: supTitle,
                                heading: HeadingLevel.HEADING_5,
                            })
                        )
                        return outputArray
                    }), linebreak()]
                })

            ],
        }]
    });

    Packer.toBlob(doc).then((blob) => {
        // saveAs from FileSaver will download the file
        saveAs(blob, filename);
    });


    // console.log(item)
}